import type { UserEntitlementView } from '@steado/admin-contract/user-detail';

import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { OwnerBadge } from '@/components/panel/OwnerBadge';
import { BadgeCell } from '@/components/table/cells';
import { formatDateTime } from '@/lib/format';

interface EntitlementPanelProps {
  readonly userId: string;
  readonly subscription: UserEntitlementView;
}

const STORE_LABELS: Readonly<Record<NonNullable<UserEntitlementView['store']>, string>> = {
  app_store: 'App Store subscription',
  play_store: 'Play Store subscription',
  promotional: 'Console grant',
};

/**
 * Where this person's access came from, and when it ends.
 *
 * The two levers moved to the record header, where somebody arriving from a
 * support thread will look for them; this card is what they read first. Access
 * and store status are stated separately on purpose — a cancellation keeps
 * Premium until the period ends, and showing only one of them is how support
 * tells somebody they have lost access they still have.
 */
export function EntitlementPanel({ userId, subscription }: EntitlementPanelProps) {
  const premium = subscription.entitlement === 'premium';

  return (
    <Panel title="Entitlement" owner={<OwnerBadge>Granted in the header</OwnerBadge>} bleed>
      <FieldRows>
        <FieldRow
          label="Access right now"
          value={<BadgeCell label={subscription.entitlement} tone={premium ? 'purple' : 'mute'} />}
        />
        <FieldRow
          label="Came from"
          value={subscription.store === null ? null : STORE_LABELS[subscription.store]}
        />
        <FieldRow label="Status at the store" value={subscription.status} />
        <FieldRow
          label="Ends"
          value={
            subscription.expiresAt === null
              ? premium
                ? 'No expiry'
                : null
              : formatDateTime(subscription.expiresAt)
          }
          mono={subscription.expiresAt !== null}
        />
        {/* The id is here rather than only in the URL: a support thread quotes it. */}
        <FieldRow label="User id" value={userId} mono />
      </FieldRows>
    </Panel>
  );
}
